import { useMemo } from "react";

import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentTalentProfile } from "@/shared";
import {
  DeckCardThumbnailRecord,
  parseDeckCardThumbnailList,
} from "../types/app-deck/deckCardThumbnail.types";
import { useGetCardThumbnails, useGetCardThumbnailsPublic } from "../api/taascard.api";

type UseDeckCardThumbnailsParams = {
  type?: string;
  enabled?: boolean;
  talentId?: number | string | null;
  /** Extra query params (e.g. purchased, status) forwarded to card-thumbnail. */
  extraParams?: Record<string, string>;
};

export function useDeckCardThumbnails({
  type,
  enabled = true,
  talentId,
  extraParams,
}: UseDeckCardThumbnailsParams) {
  const { userInfo } = useAuth();
  const isLoggedIn = Boolean(userInfo?.id);
  const { currentProfile } = useCurrentTalentProfile();
  const resolvedTalentId = talentId ? String(talentId) : "";
  const extraKey = extraParams ? JSON.stringify(extraParams) : "";
  // type is optional — omit it to fetch ALL card types (e.g. lobby All Cards).
  const queryEnabled = enabled;

  const queryParams = {
    ...(type ? { type } : {}),
    ...(resolvedTalentId ? { talent_id: resolvedTalentId } : {}),
    ...(extraParams ?? {}),
    ...(resolvedTalentId === currentProfile?.id
      ? { headers: { "talent-id": String(currentProfile.id) } }
      : {}),
  };

  const authThumbnails = useGetCardThumbnails({
    key: [
      "deck-card-thumbnails",
      type ?? "",
      resolvedTalentId,
      String(currentProfile?.id ?? ""),
      extraKey,
    ],
    params: queryParams,
    options: {
      enabled: queryEnabled && isLoggedIn,
      keepPreviousData: true,
    },
  });

  const publicThumbnails = useGetCardThumbnailsPublic({
    key: ["deck-card-thumbnails-public", type ?? "", resolvedTalentId, extraKey],
    params: queryParams,
    options: {
      enabled: queryEnabled && !isLoggedIn,
      keepPreviousData: true,
    },
  });

  const { data, isLoading, isFetching, refetch } = isLoggedIn ? authThumbnails : publicThumbnails;

  const thumbnailsByCardId = useMemo(() => {
    const map = new Map<string, DeckCardThumbnailRecord>();
    parseDeckCardThumbnailList(data).forEach((item) => {
      map.set(String(item.id), item);
    });
    return map;
  }, [data]);

  const thumbnailRecords = useMemo(() => parseDeckCardThumbnailList(data), [data]);

  return {
    thumbnailsByCardId,
    thumbnailRecords,
    isLoading: isLoading || isFetching,
    refetch,
  };
}
